import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Calendar,
  Clock,
  Phone,
  MapPin,
  GraduationCap,
  Printer,
  Bell,
  Sparkles,
  MessageCircle,
} from "lucide-react";
import "./AdmissionNoticeModal.css";

const scheduleData = [
  {
    date: "24–26/10/2026",
    time: "During Office Hours",
    activity: "Submission of applications",
    isLive: true,
  },
  {
    date: "26/10/2026",
    time: "Evening",
    activity: "Scrutiny of applications",
    isLive: false,
  },
  {
    date: "27/10/2026",
    time: "at 11.00 AM",
    activity: "Display of Provisional merit list",
    isLive: false,
  },
  {
    date: "27/10/2026",
    time: "3.00 – 5.00 PM",
    activity: "Grievances if any pertaining to provisional college merit list",
    isLive: false,
  },
  {
    date: "28/10/2026",
    time: "at 11.00 AM",
    activity: "Display of Final merit list",
    isLive: false,
  },
  {
    date: "28/10/2026 (12.00 PM Onwards) – 30/10/2026",
    time: "Up to 4.00 PM",
    activity: "Reporting to institute & Confirmation of Admission",
    isLive: false,
  },
];

const AdmissionNoticeModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const closeModal = useCallback(() => {
    if (dontShowAgain) {
      sessionStorage.setItem("dismiss_admission_notice_2026", "true");
    }
    setIsOpen(false);
  }, [dontShowAgain]);

  useEffect(() => {
    // Check if dismissed in this session
    const isDismissed = sessionStorage.getItem("dismiss_admission_notice_2026");
    if (!isDismissed) {
      // Open with smooth delay after initial page load
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen to Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeModal]);

  const openModal = () => {
    setIsOpen(true);
  };

  // Robust isolated print mechanism that avoids empty pages and prints the exact flyer layout
  const handlePrint = () => {
    let printIframe = document.getElementById("admission-print-frame");
    if (printIframe) {
      document.body.removeChild(printIframe);
    }

    printIframe = document.createElement("iframe");
    printIframe.id = "admission-print-frame";
    printIframe.style.position = "fixed";
    printIframe.style.right = "0";
    printIframe.style.bottom = "0";
    printIframe.style.width = "0";
    printIframe.style.height = "0";
    printIframe.style.border = "none";
    document.body.appendChild(printIframe);

    const printableHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>Admission Notification 2026-27 - STIPSR</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 10mm 12mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
            color: #000000;
            background: #ffffff;
            margin: 0;
            padding: 0;
            -webkit-font-smoothing: antialiased;
          }
          .flyer-card {
            border: 2.5px solid #000000;
            padding: 16px 20px;
            max-width: 100%;
          }
          .header-center {
            text-align: center;
            border-bottom: 2px solid #000000;
            padding-bottom: 10px;
            margin-bottom: 12px;
          }
          .trust {
            font-size: 13pt;
            font-weight: 700;
            margin: 0 0 3px 0;
          }
          .college {
            font-size: 16pt;
            font-weight: 900;
            margin: 0 0 4px 0;
          }
          .place {
            font-size: 12pt;
            font-weight: 700;
            margin: 0 0 4px 0;
          }
          .approvals {
            font-size: 10.5pt;
            font-weight: 600;
            margin: 0;
          }
          .announcement {
            text-align: center;
            margin: 14px 0;
          }
          .main-heading {
            font-size: 16pt;
            font-weight: 900;
            letter-spacing: 0.5px;
            margin: 0 0 4px 0;
          }
          .sub-heading {
            font-size: 12pt;
            font-weight: 700;
            margin: 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
            font-size: 10.5pt;
          }
          table, th, td {
            border: 1.5px solid #000000;
          }
          th {
            padding: 8px 10px;
            background-color: #f1f1f1 !important;
            font-weight: 800;
            text-align: center;
            font-size: 11pt;
          }
          td {
            padding: 8px 12px;
            vertical-align: middle;
          }
          .course-table td:nth-child(1),
          .course-table td:nth-child(2) {
            text-align: center;
            font-weight: 700;
          }
          .statement {
            font-size: 11pt;
            font-weight: 700;
            line-height: 1.5;
            margin: 10px 0 6px 0;
          }
          .schedule-table td:nth-child(1) {
            width: 42%;
            font-weight: 800;
            text-align: center;
          }
          .schedule-table td:nth-child(2) {
            font-weight: 600;
          }
          .contact-section {
            border-top: 2px solid #000000;
            margin-top: 14px;
            padding-top: 10px;
            text-align: center;
            font-size: 11pt;
            line-height: 1.45;
          }
          .contact-title {
            font-weight: 800;
            margin-bottom: 2px;
          }
          .contact-phones {
            font-weight: 900;
            font-size: 12pt;
            margin-top: 3px;
          }
        </style>
      </head>
      <body>
        <div class="flyer-card">
          <div class="header-center">
            <div class="trust">Shri. G. S. Tompe Mahavidyalaya Public Trust</div>
            <div class="college">Sumatidevi Tompe Institute of Pharmaceutical Science & Research (01341)</div>
            <div class="place">Chandur Bazar, Amravati.</div>
            <div class="approvals">(Approved by PCI & Govt. of Maharashtra, Affiliated to MSBTE)</div>
          </div>

          <div class="announcement">
            <div class="main-heading">ADMISSION NOTIFICATION 2026–27</div>
            <div class="sub-heading">For Institute Level Seats & Vacant Seats after CAP Round</div>
          </div>

          <table class="course-table">
            <thead>
              <tr>
                <th style="width: 26%;">Course</th>
                <th style="width: 24%;">Duration</th>
                <th style="width: 50%;">Eligibility</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>D. Pharmacy</td>
                <td>2 years</td>
                <td>12<sup>th</sup> Science Passed with Physics, Chemistry, Biology or Maths</td>
              </tr>
            </tbody>
          </table>

          <p class="statement">
            Applications are invited from the eligible students for First Year D. Pharmacy admission for Institute level seats & vacant seats after CAP round.
          </p>
          <p class="statement">
            Application forms are available at the institute office.
          </p>

          <table class="schedule-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>24–26/10/2026</td>
                <td>Submission of applications</td>
              </tr>
              <tr>
                <td>26/10/2026</td>
                <td>Scrutiny of applications</td>
              </tr>
              <tr>
                <td>27/10/2026 at 11.00 AM</td>
                <td>Display of Provisional merit list</td>
              </tr>
              <tr>
                <td>27/10/2026 3.00–5.00 PM</td>
                <td>Grievances if any pertaining to provisional college merit list</td>
              </tr>
              <tr>
                <td>28/10/2026 at 11.00 AM</td>
                <td>Display of Final merit list</td>
              </tr>
              <tr>
                <td>28/10/2026 (12.00 PM Onwards)–<br/>30/10/2026 (Up to 4.00 PM)</td>
                <td>Reporting to institute & Confirmation of Admission</td>
              </tr>
            </tbody>
          </table>

          <div class="contact-section">
            <div class="contact-title">For more details, please visit or contact us:</div>
            <div>Shri. G. S. Tompe Educational Campus, Nanori Road, Chandur Bazar – 444704 Dist. Amravati. (M.S.)</div>
            <div class="contact-phones">+91 97669 38363 / +91 8551 954921</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const doc = printIframe.contentWindow.document;
    doc.open();
    doc.write(printableHtml);
    doc.close();

    setTimeout(() => {
      printIframe.contentWindow.focus();
      printIframe.contentWindow.print();
    }, 250);
  };

  return (
    <>
      {/* ================= POPUP MODAL ================= */}
      {isOpen && (
        <div
          className="admission-modal-overlay"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admission-notice-title"
        >
          <div
            className="admission-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Close Button */}
            <button
              className="admission-modal-close-btn"
              onClick={closeModal}
              title="Close Notice (Esc)"
              aria-label="Close Notice"
            >
              <X size={20} />
            </button>

            {/* Header with College Crest & Approvals */}
            <div className="admission-modal-topbar">
              <div className="admission-header-content">
                <img
                  src="/new-header-logo-removebg-preview.png"
                  alt="STIPSR Logo"
                  className="admission-college-logo"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="admission-header-text">
                  <p className="admission-trust-name">
                    Shri. G. S. Tompe Mahavidyalaya Public Trust
                  </p>
                  <h1 className="admission-college-title">
                    Sumatidevi Tompe Institute of Pharmaceutical Science & Research
                    <span className="admission-code-tag">Code: 01341</span>
                  </h1>
                  <p className="admission-location-text">
                    📍 Chandur Bazar, Amravati.
                  </p>
                  <div className="admission-approval-pills">
                    <span className="admission-approval-pill">
                      Approved by PCI
                    </span>
                    <span className="admission-approval-pill">
                      Govt. of Maharashtra
                    </span>
                    <span className="admission-approval-pill">
                      Affiliated to MSBTE
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="admission-modal-body">
              {/* Highlight Banner */}
              <div className="admission-notice-banner">
                <div className="admission-badge-glow">
                  <Sparkles size={13} />
                  <span>Important Announcement 2026–27</span>
                </div>
                <h2 id="admission-notice-title">
                  ADMISSION NOTIFICATION 2026–27
                </h2>
                <p>
                  For Institute Level Seats & Vacant Seats after CAP Round
                </p>
              </div>

              {/* Table 1: Course, Duration & Eligibility */}
              <div className="admission-course-card">
                <table className="admission-table">
                  <thead>
                    <tr>
                      <th style={{ width: "28%" }}>Course</th>
                      <th style={{ width: "22%" }}>Duration</th>
                      <th style={{ width: "50%" }}>Eligibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="admission-course-name">
                          <GraduationCap size={20} className="text-blue-700" />
                          <span>D. Pharmacy</span>
                        </div>
                      </td>
                      <td>
                        <span className="admission-duration-badge">
                          2 Years
                        </span>
                      </td>
                      <td>
                        <span className="admission-eligibility-highlight">
                          <strong>12th Science Passed</strong> with Physics,
                          Chemistry, Biology or Maths
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Invitation Statement */}
              <div className="admission-statement-box">
                <p>
                  📌 <strong>Applications are invited</strong> from the eligible
                  students for First Year <strong>D. Pharmacy</strong> admission
                  for <strong>Institute level seats & vacant seats</strong> after
                  CAP round.
                </p>
                <p>
                  📋 <strong>Application forms</strong> are available at the
                  institute office during working hours.
                </p>
              </div>

              {/* Table 2: Schedule of Activities */}
              <div className="admission-schedule-section">
                <h3 className="admission-section-heading">
                  <Calendar size={18} className="text-amber-600" />
                  Schedule of Admission Activities
                </h3>
                <table className="admission-schedule-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleData.map((item, index) => (
                      <tr key={index}>
                        <td className="admission-date-cell">
                          <div
                            className={`admission-date-badge ${
                              item.isLive ? "admission-date-highlight" : ""
                            }`}
                          >
                            <Calendar size={14} />
                            <span>{item.date}</span>
                          </div>
                          {item.time && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1 pl-1">
                              <Clock size={12} />
                              <span>{item.time}</span>
                            </div>
                          )}
                        </td>
                        <td className="admission-activity-cell">
                          <span>{item.activity}</span>
                          {item.isLive && (
                            <span className="admission-live-tag">
                              ● Ongoing / Active
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Contact and Location Card */}
              <div className="admission-contact-card">
                <h4 className="admission-contact-title">
                  For More Details, Please Visit or Contact Us:
                </h4>
                <div className="admission-contact-grid">
                  <div className="admission-address-item">
                    <MapPin size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Shri. G. S. Tompe Educational Campus</strong>
                      <br />
                      Nanori Road, Chandur Bazar – 444704
                      <br />
                      Dist. Amravati. (M.S.)
                    </div>
                  </div>
                  <div className="admission-phones-item">
                    <a
                      href="tel:+919766938363"
                      className="admission-phone-btn"
                    >
                      <Phone size={15} className="text-blue-700" />
                      <span>+91 97669 38363</span>
                    </a>
                    <a
                      href="tel:+918551954921"
                      className="admission-phone-btn"
                    >
                      <Phone size={15} className="text-blue-700" />
                      <span>+91 8551 954921</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Bottom Actions */}
            <div className="admission-modal-footer">
              <label className="admission-dont-show-label">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Don't show popup again during this visit
              </label>

              <div className="admission-action-buttons">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="admission-btn admission-btn-print"
                  title="Print Official Notice"
                >
                  <Printer size={15} />
                  <span>Print Notice</span>
                </button>

                <a
                  href="https://chat.whatsapp.com/KZkdOH1gmt85ekIh0pe6AJ?mode=ac_t"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admission-btn"
                  style={{
                    backgroundColor: "#16a34a",
                    color: "#ffffff",
                  }}
                  title="Chat on WhatsApp"
                >
                  <MessageCircle size={15} />
                  <span>WhatsApp</span>
                </a>

                <button
                  type="button"
                  onClick={closeModal}
                  className="admission-btn admission-btn-close"
                >
                  <span>Close</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= FLOATING RE-OPEN BADGE ================= */}
      {/* Allows visitors to re-open the notification notice at any time after closing */}
      {!isOpen && (
        <button
          className="admission-floating-trigger"
          onClick={openModal}
          title="Click to view Admission Notification 2026-27"
          aria-label="View Admission Notification"
        >
          <div className="admission-floating-pulse" />
          <Bell size={18} />
          <span>Admission Notice 2026–27</span>
        </button>
      )}
    </>
  );
};

export default AdmissionNoticeModal;
